import { CaseUser, UserRole } from "./caseUser"

// Data Types are for the underlying values contained within fields.
// Multiple fields can match to the same DataType, for example, both number and dollar both map to number

// See SVCases.Models.Search.DataTypes in the data service for a matching definition
export enum DataType {
	string = 0,
	dateTime = 1,
	number = 2,
	boolean = 3,
	array = 4,
	arrayMultiple = 5,
	arrayData = 6,
	calculated = 7,
	none = 8,
	userRole = 9,
	mapPoint = 10,
	bounds = 11,
	timeSpentAsValue = 12,
	team = 13,
	dateRange = 14,
	subrole = 15
}

// New Data Type checklist:
// - Add to enum (Above)
// - Add to SVCases.Models.Search.DataTypes enum in Data Service
// - Add grouping function to groupingFunctions (below) (If relevant)
// - Add operators to dataTypeOperations in @/types/operators.ts
// - Add to dataTypeTests in @/types/operators.ts
// - Add to DataTypeFunctions in @/scripts/dataTypeFunctions.ts

// Function for grouping by data types
export const groupingFunctions: { [key in DataType]?: (record: any, groupByValue: any, cache: { [key:string]: any }, groupByOption?: string | null) => { key: string, label: string, value: any }[]} = {
	[DataType.userRole]: (record: any, groupByValue: UserRole, cache: { [key:string]: any }, groupByOption) => {
		const mapUsers: (users: CaseUser[]) => { key: string, label: string, value: any }[] =
			users => users.map(user => {
				const userValue = cache[user.id]
					? cache[user.id]
					: cache[user.id] = record.users.find((u: CaseUser) => u.id === user.id)
				return {
					key: user.id,
					label: userValue ? userValue.name : user.id,
					value: userValue
				}
			})

		let returnValues = groupByValue ? [
			...(groupByValue.users ? mapUsers(groupByValue.users) : []),
			...(groupByValue.subRoles
				? groupByValue.subRoles.map(role => role.users ? mapUsers(role.users) : []).flat()
				: [])
		] : []

		if (groupByOption === "team") {
			returnValues = returnValues.filter(u => u.value && u.value.teams)
				.map(u => u.value.teams.map((team: any) => {
					return {
						key: team.id,
						label: team.title,
						value: team
					}
				}))
				.flat()
		}

		return returnValues.length === 0
			? [
				{
					key: "none",
					label: "None set",
					value: null
				}
			]
			: returnValues
	}
}

export const fieldTypesExcludeFromRuleSet = [
	"calculated",
	"list-multiple",
	"user-role",
	"list-data",
	"case-number",
	"time-spent",
	"file-only"
]

export const groupByOptions: { [key in DataType]?: {value: string, label: string}[]} = {
	[DataType.userRole]: [
		{ value: "user", label: "User" },
		{ value: "team", label: "Team" }
	]
}

export const groupingOptions = {
	subRole: "subRole"
}

export const dataTypeByFieldType: { [k: string]: DataType } = {
	"text": DataType.string,
	"text-area": DataType.string,
	"number": DataType.number,
	"dollar": DataType.number,
	"date": DataType.dateTime,
	"date-time": DataType.dateTime,
	"checkbox": DataType.boolean,
	"list": DataType.array,
	"list-multiple": DataType.arrayMultiple,
	"list-data": DataType.arrayData,
	"user-role": DataType.userRole,
	"map-marker": DataType.mapPoint,
	"calculated": DataType.number,
	"external-link": DataType.string,
	"case-number": DataType.string,
	"time-spent": DataType.timeSpentAsValue,
	"file-only": DataType.none,
	"icon-upload": DataType.string
}

export const dataTypeParameters = [
	{
		label: "Text",
		dataType: DataType.string,
		selectSource: false,
		fieldDataTypes: [DataType.string]
	},
	{
		label: "Number",
		dataType: DataType.number,
		selectSource: false,
		fieldDataTypes: [DataType.number]
	},
	{
		label: "Date",
		dataType: DataType.dateTime,
		selectSource: false,
		fieldDataTypes: [DataType.dateTime]
	},
	{
		label: "Date Range",
		dataType: DataType.dateRange,
		selectSource: false,
		fieldDataTypes: [DataType.dateTime]
	},
	{
		label: "Data Source",
		dataType: DataType.array,
		selectSource: true,
		fieldDataTypes: [DataType.array, DataType.arrayMultiple, DataType.arrayData]
	},
	{
		label: "User",
		dataType: DataType.userRole,
		selectSource: false,
		fieldDataTypes: [DataType.userRole]
	}
]

export const dataTypes = {
	Text: "text",
	TextArea: "text-area",
	Number: "number",
	Dollar: "dollar",
	Date: "date",
	DateTime: "date-time",
	Checkbox: "checkbox",
	List: "list",
	ListMultiple: "list-multiple",
	DataObject: "list-data",
	UserRole: "user-role",
	MapMarker: "map-marker",
	Calculated: "calculated",
	ExternalLink: "external-link",
	CaseNumber: "case-number",
	TimeSpent: "time-spent",
	FileOnly: "file-only",
	Icon: "icon-upload"
} as const
